import '@components/Home/Content.scss';
import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { change } from 'redux-form';
import { useHistory, withRouter } from 'react-router-dom';
import { checkUser } from '@utils/reduxUtils';
import queryString from 'query-string';
import { BrandType } from '../../redux/actions';
import { useAuth0 } from '@auth0/auth0-react';
import { Link } from '@components/typography/Link';
import { currency } from '@components/_fields/normalizers';
import { Text, Stack, StepCard, Embed, Conduit, Growth, useResponsiveBreakpoints } from 'shared-components';
import { formValueSelector, reset } from 'redux-form';
import { useDispatch } from 'react-redux';
import { useApplyWizardContext } from '@components/../context/ApplyWizardContext';
import { RootState } from '@store/reducers';
import { Helmet } from 'react-helmet';

const BCMApplyEngageExpand = ({
  user,
  loanId,
  content,
}: {
  user: RootState['user'];
  loanId: string;
  content?: BrandType['data']['content'];
}) => {
  const history = useHistory();
  const auth0 = useAuth0();
  const breakpoints = useResponsiveBreakpoints();
  const dispatch = useDispatch();
  const wizardContext = useApplyWizardContext();
  useEffect(() => {
    dispatch(change('application', 'application_type', 'bcm'));
  }, []);

  console.log('user', auth0.user);
  // TODO: Implement check of current status of request, if it is requested or approved move the user to relevant page.

  return (
    <Stack
      flow="column"
      gap={0}
      alignItems="center"
      justifyContent="center"
      templateColumns={breakpoints.isMobile ? '280px' : '230px 280px 230px'}
    >
      {!breakpoints.isMobile && (
        <StepCard
          type="completed"
          headline="Engage"
          number="01"
          textBody={content?.home?.boxOneText ?? 'Spot risks before they hit.'}
          icon={<Embed fill="var(--color-spark)" stroke="var(--color-charcoal)" />}
          mainContainerProps={{
            className: 'justify-between',
          }}
          cardProps={{
            size: 'small',
            className: 'border-none rounded-r-none !text-dawn',
            initial: { opacity: 0, top: '50px' },
            animate: { opacity: 1, top: 0 },
            transition: { duration: 0.5, delay: 1 },
          }}
          headlineProps={{ className: '!text-dawn' }}
          textBodyProps={{ className: 'text-dawn' }}
          numberProps={{ className: '!text-dawn' }}
        />
      )}
      <StepCard
        type="current"
        headline="Activate"
        number={breakpoints.isMobile ? '' : '02'}
        textBody={content?.home?.boxTwoText ?? 'Get started today with free access.'}
        icon={<Conduit vector1Fill="var(--color-spark)" />}
        button="SEE MY SCORE"
        mainContainerProps={{
          className: 'justify-between',
        }}
        buttonProps={{
          className: 'font-condensed padding-[1rem]',
          onClick: () => {
            if (wizardContext.applicationFlow === 'bcm' || wizardContext.applicationFlow === 'bcm-short') {
              if (
                auth0 &&
                auth0.isAuthenticated &&
                auth0.user &&
                auth0.user['https://tento.co/accounts'] &&
                auth0.user['https://tento.co/accounts'].length > 0
              ) {
                history.push('/bcm2/business/select');
              } else {
                if (wizardContext.changeStepToFirst) {
                  wizardContext.changeStepToFirst();
                }
              }
            } else {
              if (user.loggedIn && loanId) {
                // If user is logged in and has a loanId, we want to continue the existing application.
              } else {
                dispatch(reset('application'));
                dispatch(change('application', 'loan_amount', 150000));
                dispatch(change('application', 'loan_terms', 60));
                dispatch(change('application', 'owner_1_email', user.loggedIn ? user.email : ''));
                if (wizardContext.resetFarthestStep) {
                  wizardContext.resetFarthestStep();
                }
              }
              if (wizardContext.changeStepToFirst) {
                wizardContext.changeStepToFirst();
              }
            }
          },
        }}
        cardProps={{
          className: 'border-none py-[20px]',
          initial: { opacity: 0, top: '50px' },
          animate: { opacity: 1, top: 0 },
          transition: { duration: 0.5, delay: 1 },
        }}
        numberProps={{ style: { top: '24px' } }}
      />

      {!breakpoints.isMobile && (
        <StepCard
          type="completed"
          headline="Expand"
          number="03"
          textBody={content?.home?.boxThreeText ?? 'Fuel growth with credit insights.'}
          mainContainerProps={{
            className: 'justify-between',
          }}
          icon={
            <Growth
              vector1Fill="var(--color-spark)"
              vector1Stroke="var(--color-charcoal)"
              stroke="var(--color-bg-dawn-2)"
            />
          }
          cardProps={{
            size: 'small',
            className: 'border-none rounded-l-none !text-dawn',
            initial: { opacity: 0, top: '50px' },
            animate: { opacity: 1, top: 0 },
            transition: { duration: 0.5, delay: 1 },
          }}
          headlineProps={{ className: '!text-dawn' }}
          textBodyProps={{ className: 'text-dawn' }}
          numberProps={{ className: '!text-dawn' }}
        />
      )}
    </Stack>
  );
};

const Home = ({
  loanId,
  user,
  match,
  history,
  dispatch,
  location,
  change,
  content,
}: {
  loanId: string;
  user: RootState['user'];
  match: any;
  history: any;
  dispatch: any;
  location: any;
  change: any;
  content?: BrandType['data']['content'];
}) => {
  const { logout } = useAuth0();

  useEffect(() => {
    if (location && location.search) {
      const query = location.search;
      // save ga_info
      const queryObj = JSON.parse(JSON.stringify(queryString.parse(query)));
      if (queryObj.hasOwnProperty('utm_source')) {
        change('application', 'ga_info', JSON.stringify(queryObj));
      }
    }

    checkUser(user, match, dispatch, history, logout);

    if (process.env.STORYBOOK_TEST) return;
  }, []);

  return (
    <>
      <Helmet>
        <title>Sign up for Business Credit Monitoring</title>
        {/* Open Graph */}
        <meta property="og:title" content="Sign up for Business Credit Monitoring" />
        <meta
          property="og:description"
          content="The credit booster built for business owners. With Tento as your co-pilot, you can build business credit, secure better financing, and scale smarter"
        />
        <meta
          property="og:image"
          content="https://media.tento.co/apply/partners/brands/tento-apply-app/favicon-new.png"
        />
        <meta property="og:type" content="website" />

        {/* Twitter */}
        <meta property="twitter:title" content="Sign up for Business Credit Monitoring" />
        <meta
          property="twitter:description"
          content="The credit booster built for business owners. With Tento as your co-pilot, you can build business credit, secure better financing, and scale smarter"
        />
        <meta
          property="twitter:image"
          content="https://media.tento.co/apply/partners/brands/tento-apply-app/favicon-new.png"
        />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>
      <Stack gapVariant="head-section-to-content" textAlign="center">
        <Stack gapVariant="headline-to-subheadline" textAlign="center">
          <div>
            <div className="max-w-[580px] mx-auto">
              <Text variant="headline-lg">{content?.home?.title ?? 'View Your Business Credit Score For Free'}</Text>
            </div>
          </div>
          <div>
            <Text variant="subheadline-lg">
              {content?.home?.subtitle ??
                'Fraud detection, credit building insights, and advanced monitoring at your finger tips.'}
            </Text>
          </div>
        </Stack>
        <BCMApplyEngageExpand user={user} loanId={loanId} content={content} />
      </Stack>
    </>
  );
};
const appSelector = formValueSelector('application');

const mapStateToProps = (state: RootState) => {
  return {
    user: state.user,
    theme: state.theme,
    loanAmount: appSelector(state, 'loan_amount'),
    loanId: appSelector(state, 'loan_id'),
    businessName: appSelector(state, 'business_name'),
    content: state.brand?.data?.bcmContent ?? undefined,
  };
};

const HomeConnected = connect(mapStateToProps, (dispatch) => ({
  change,
  dispatch,
}))(Home);

export const BCMHome = withRouter(HomeConnected);

export default BCMHome;
